import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "../LangContext";
import { Linkedin } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CATEGORY_ORDER = ["founder", "board", "operational", "ambassadors", "advisors"];
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

const initials = (name) =>
    name.split(" ").map((n) => n[0]).join("").slice(0, 3);

// Multilingual field picker with EN fallback (tolerates legacy plain strings)
const makePick = (lang) => (f) =>
    f && typeof f === "object" ? (f[lang.toLowerCase()] || f.en || "") : f || "";

export default function Team() {
    const { t, lang } = useLang();
    const [members, setMembers] = useState([]);
    const [selected, setSelected] = useState(null);
    const pick = makePick(lang);

    useEffect(() => {
        axios.get(`${API}/team`).then((r) => setMembers(r.data)).catch(() => {});
    }, []);

    // Group by category, preserving CATEGORY_ORDER
    const grouped = CATEGORY_ORDER.map((cat) => ({
        cat,
        members: members
            .filter((m) => m.category === cat)
            .sort((a, b) => (a.order || 0) - (b.order || 0)),
    })).filter((g) => g.members.length > 0);

    if (grouped.length === 0) return null;

    return (
        <section id="team" data-testid="team-section" className="py-24 md:py-32 px-6 md:px-12 bg-inksurface">
            <div className="max-w-7xl mx-auto">
                <div className="mb-16 max-w-3xl">
                    <span className="text-xs font-mono uppercase tracking-[0.3em] text-volt">— {t.team.label}</span>
                    <h2 className="font-graffiti text-4xl md:text-6xl tracking-tight mt-6 text-white leading-[0.95]">
                        {t.team.title}
                    </h2>
                </div>

                {grouped.map((g) => (
                    <div key={g.cat} data-testid={`team-cat-${g.cat}`} className="mb-16 last:mb-0">
                        <div className="mb-6 flex items-center gap-4">
                            <div className="h-px flex-1 bg-white/10" />
                            <h3 className="font-graffiti text-2xl md:text-3xl text-volt tracking-tight text-center">
                                {t.team.categories?.[g.cat] || g.cat}
                            </h3>
                            <div className="h-px flex-1 bg-white/10" />
                        </div>

                        {/* Founder gets a wider hero card if present */}
                        {g.cat === "founder" && g.members.length === 1 ? (
                            <FounderCard m={g.members[0]} t={t} pick={pick} onOpen={setSelected} />
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
                                {g.members.map((m) => (
                                    <MemberCard key={m.id} m={m} t={t} pick={pick} onOpen={setSelected} />
                                ))}
                            </div>
                        )}
                    </div>
                ))}

                <div className="mt-4">
                    <a href="#donate" data-testid="team-cta" className="btn-ghost inline-flex items-center gap-2 px-6 py-3 text-xs uppercase font-bold tracking-wider">
                        {t.nav.help} →
                    </a>
                </div>
            </div>

            <MemberDialog m={selected} t={t} pick={pick} onClose={() => setSelected(null)} />
        </section>
    );
}

const MemberCard = ({ m, t, pick, onOpen }) => {
    const role = pick(m.role);
    const motivation = pick(m.motivation);
    const bio = pick(m.bio);
    return (
        <div
            data-testid={`team-member-${m.id}`}
            role="button"
            tabIndex={0}
            onClick={() => onOpen(m)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onOpen(m); }}
            className="bg-ink p-6 md:p-8 group hover:bg-inksurface transition-colors flex flex-col cursor-pointer"
        >
            <div className="aspect-[4/5] md:aspect-square w-full bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center mb-5 border border-white/5 overflow-hidden relative">
                {m.photo_url ? (
                    <img
                        src={resolveUrl(m.photo_url)}
                        alt={m.name}
                        className="w-full h-full object-contain md:object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-700"
                    />
                ) : (
                    <div className="font-graffiti text-6xl md:text-7xl text-volt/30 group-hover:text-volt transition-colors">
                        {initials(m.name)}
                    </div>
                )}
            </div>
            <h4 className="font-display font-bold text-white text-base md:text-lg tracking-tight">{m.name}</h4>
            {role && <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">{role}</p>}
            {motivation && (
                <p data-testid={`team-motivation-${m.id}`} className="mt-3 text-xs text-volt italic leading-relaxed line-clamp-3">
                    "{motivation}"
                </p>
            )}
            <div className="mt-auto pt-4 flex items-center justify-between gap-2">
                {bio ? (
                    <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-600 group-hover:text-volt transition-colors">
                        {t.team.read_bio} →
                    </span>
                ) : <span />}
                {m.linkedin && (
                    <a
                        href={m.linkedin}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`team-linkedin-${m.id}`}
                        aria-label={`${m.name} on LinkedIn`}
                        className="inline-flex items-center justify-center w-9 h-9 border border-white/10 text-zinc-400 hover:border-volt hover:text-volt transition-colors"
                    >
                        <Linkedin size={16} />
                    </a>
                )}
            </div>
        </div>
    );
};

const FounderCard = ({ m, t, pick, onOpen }) => {
    const role = pick(m.role);
    const motivation = pick(m.motivation);
    const bio = pick(m.bio);
    return (
        <div
            data-testid={`team-founder-${m.id}`}
            role="button"
            tabIndex={0}
            onClick={() => onOpen(m)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onOpen(m); }}
            className="border border-white/10 bg-ink grid grid-cols-1 lg:grid-cols-12 gap-0 overflow-hidden cursor-pointer group hover:border-volt/40 transition-colors"
        >
            <div className="lg:col-span-5 aspect-[4/5] lg:aspect-auto bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center overflow-hidden">
                {m.photo_url ? (
                    <img src={resolveUrl(m.photo_url)} alt={m.name} className="w-full h-full object-contain lg:object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-700" />
                ) : (
                    <div className="font-graffiti text-9xl text-volt/30">{initials(m.name)}</div>
                )}
            </div>
            <div className="lg:col-span-7 p-8 md:p-12 flex flex-col justify-center">
                <h4 className="font-graffiti text-4xl md:text-5xl text-white leading-tight tracking-tight">{m.name}</h4>
                {role && <p className="text-xs text-volt uppercase tracking-[0.3em] mt-3 font-mono">{role}</p>}
                {motivation && (
                    <blockquote data-testid={`team-motivation-${m.id}`} className="mt-6 border-l-2 border-volt pl-5 text-volt italic text-base md:text-lg leading-relaxed">
                        "{motivation}"
                    </blockquote>
                )}
                <div className="mt-6 flex items-center justify-between gap-3">
                    {bio ? (
                        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-600 group-hover:text-volt transition-colors">
                            {t.team.read_bio} →
                        </span>
                    ) : <span />}
                    {m.linkedin && (
                        <a
                            href={m.linkedin}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`${m.name} on LinkedIn`}
                            className="inline-flex items-center justify-center w-10 h-10 border border-white/10 text-zinc-400 hover:border-volt hover:text-volt transition-colors"
                        >
                            <Linkedin size={18} />
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

const MemberDialog = ({ m, t, pick, onClose }) => {
    if (!m) return null;
    const role = pick(m.role);
    const motivation = pick(m.motivation);
    const bio = pick(m.bio);
    return (
        <Dialog open={!!m} onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent
                data-testid="team-member-dialog"
                aria-describedby={undefined}
                className="bg-ink border-white/10 text-white w-[calc(100vw-2rem)] max-w-2xl p-0 sm:rounded-none max-h-[85vh] overflow-y-auto"
            >
                <div className="grid grid-cols-1 md:grid-cols-5">
                    <div className="md:col-span-2 h-[38vh] min-h-[260px] max-h-[380px] md:h-auto md:min-h-[320px] md:max-h-none bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center overflow-hidden">
                        {m.photo_url ? (
                            <img src={resolveUrl(m.photo_url)} alt={m.name} className="w-full h-full object-contain md:object-cover object-center md:object-top" />
                        ) : (
                            <div className="font-graffiti text-8xl text-volt/30">{initials(m.name)}</div>
                        )}
                    </div>
                    <div className="md:col-span-3 p-6 md:p-8">
                        <DialogTitle className="font-graffiti text-3xl md:text-4xl text-white leading-tight tracking-tight">
                            {m.name}
                        </DialogTitle>
                        {role && <p className="text-xs text-volt uppercase tracking-[0.3em] mt-2 font-mono">{role}</p>}
                        {motivation && (
                            <blockquote className="mt-5 border-l-2 border-volt pl-4 text-volt italic text-sm leading-relaxed">
                                "{motivation}"
                            </blockquote>
                        )}
                        {bio ? (
                            <p data-testid="team-dialog-bio" className="mt-5 text-sm text-zinc-300 leading-relaxed whitespace-pre-line">{bio}</p>
                        ) : (
                            <p data-testid="team-dialog-bio" className="mt-5 text-sm text-zinc-500 italic">{t.team.no_bio}</p>
                        )}
                        {m.linkedin && (
                            <a
                                href={m.linkedin}
                                target="_blank"
                                rel="noreferrer"
                                aria-label={`${m.name} on LinkedIn`}
                                className="mt-6 inline-flex items-center justify-center w-10 h-10 border border-white/10 text-zinc-400 hover:border-volt hover:text-volt transition-colors"
                            >
                                <Linkedin size={18} />
                            </a>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
